//@ts-check
import { Controller } from "@hotwired/stimulus";
import {
  AddressComponent,
  attrFromName,
} from "store/javascripts/components/checkout/AddressComponent";
import { BillingLicenseeToggleComponent } from "../components/checkout/BillingLicenseeToggleComponent";
import { BillingShippingToggleComponent } from "../components/checkout/BillingShippingToggleComponent";
import { CheckoutPageState } from "../components/checkout/CheckoutPageState";
import { LicenseUsageSelectorComponent } from "../components/checkout/LicenseUsageSelectorComponent";
import { PartnerLicenseToggleComponent } from "../components/checkout/PartnerLicenseToggleComponent";

/**
 * @typedef {import("@hotwired/stimulus").Controller & { vatUpdateUrlValue: string }} CheckoutFormControllerInstance
 */

/**
 * Coordinates toggles and addresses on the Cart/Checkout page.
 *
 * - Inject the "Different License holder" and "Different Shipping Address" toggles
 * - Show/Hide different addresses (billing/licensee/shipping)
 * - Mirrors changes from one address field group to another when they need to be the same.
 *
 */
export default class extends Controller {
  static values = { vatUpdateUrl: String };
  //  -------------------- lifecycle  --------------------

  connect() {
    // @ts-ignore
    window.checkoutFormController = this;

    this.form = /** @type {HTMLFormElement} */ (this.element);
    this.cartElement = /** @type {HTMLElement | null} */ (
      this.element.closest(".shopping-cart-page")
    );
    this.addressesElement = /** @type {HTMLElement | null} */ (
      this.form.querySelector(".checkout-addresses")
    );
    if (!this.addressesElement) return;

    // Normalize boolean data attrs (Stimulus gives them as strings).
    this.mode = this.addressesElement.dataset.checkoutAddressesMode;
    this.requiresLicensing =
      this.cartElement.dataset.requiresLicensing === "true";
    this.requiresShipping =
      this.cartElement.dataset.requiresShipping === "true";

    // Whether the cart contains the seeded "Branding" licensing medium.
    this.brandingLicense = this.cartElement.dataset.brandingLicense === "true";

    this.addresses = /** @type {Record<string, AddressComponent>} */ ({});

    this._onAddressChange = this.onAddressChange.bind(this);
    this.addressesElement
      .querySelectorAll("section[data-address-role]")
      .forEach((s) => {
        const comp = new AddressComponent(s, this._onAddressChange);
        this.addresses[comp.role] = comp;
      });

    /** @type {CheckoutPageState} */
    this.state = this.initState();

    this.initUI();

    this.handleLicenseUsageChange();

    this.applyState();
  }

  disconnect() {
    this.removeListeners();
  }

  //  -------------------- state model --------------------

  /**
   * State shape:
   *
   * - licenseUsage: 'me' | 'client'
   * - licenseUsageClientName: string
   * - shouldIssuePartnerLicense: boolean
   * - isLicenseeAddressDifferent
   * - isShippingAddressDifferent
   */
  initState() {
    const { shipping, billing, licensee } = this.addresses;

    const licenseForClient =
      this.licenseUsageSelectorElement?.querySelector(
        'input[name="license_usage"]:checked',
        // @ts-ignore
      )?.value === "client";

    const licenseUsageClientInput = /** @type {HTMLInputElement | null} */ (
      this.licenseUsageSelectorElement?.querySelector(
        'input[name="license_usage_client_name"]',
      )
    );

    return new CheckoutPageState({
      isBillingLicenseeMerged:
        (this.requiresLicensing && licensee?.equals(billing)) ?? false,
      isBillingShippingMerged: shipping?.equals(billing) ?? false,
      licenseUsage: licenseForClient ? "client" : "me",
      licenseUsageClientName: licenseUsageClientInput?.value || "",
      // Initial value comes from a server-rendered data attribute.
      // The visible checkbox itself is name-bound and submits directly.
      shouldIssuePartnerLicense:
        this.cartElement.dataset.shouldIssuePartnerLicense === "true",
    });
  }

  get partnerLicenseButton() {
    return /** @type {HTMLElement | null} */ (
      this.form.querySelector('[data-id="partner-license-button"]')
    );
  }

  // Reset the main "I agree with..." checkbox to unchecked.
  uncheckLicenseAgreement() {
    const checkbox = /** @type {HTMLInputElement | null} */ (
      this.form.querySelector(
        'input[type="checkbox"][name="order[license_agreement]"]',
      )
    );
    if (checkbox) checkbox.checked = false;
  }

  //  -------------------- UI --------------------

  // Inserts merge-toggle checkboxes, and alternate-role radio groups
  // into each section. The markup is owned by JS, not the server, so the server template
  // stays a trivial 3-section list.
  initUI() {
    this.injectBillingLicenseeToggle();
    this.injectBillingShippingToggle();
    this.injectPartnerLicenseToggle();
    this.initLicenseUsageSelector();
    this._uiInitialized = true;
  }

  // Brand Partner License checkbox lives inside the billing block.
  // Only meaningful when the cart includes a Branding medium.
  injectPartnerLicenseToggle() {
    if (!this.brandingLicense) return;

    this.partnerLicenseToggle = new PartnerLicenseToggleComponent({
      shouldIssue: this.state.shouldIssuePartnerLicense,
      label: this.cartElement.dataset.partnerLicenseOptOutLabel,
      onChange: (shouldIssue) => {
        if (!this._uiInitialized) return;
        this.state.shouldIssuePartnerLicense = shouldIssue;

        // While the Brand Partner license is being issued, a new license button
        // appears in the agreement label, so the user must re-confirm: uncheck
        // the main "I agree with..." checkbox.
        if (shouldIssue) this.uncheckLicenseAgreement();
        this.applyState();
      },
    });
    this.addresses.billing.addToggle(this.partnerLicenseToggle);
  }

  injectBillingLicenseeToggle() {
    if (!this.requiresLicensing) return;

    this.billingLicenseeToggle = new BillingLicenseeToggleComponent({
      checked: !this.state.isBillingLicenseeMerged,
      isPurchasingForClient: this.state.licenseUsage === "client",
      onChange: (checked) => {
        if (!this._uiInitialized) return;
        this.state.isBillingLicenseeMerged = !checked;
        this.applyState();
        setTimeout(() => this.handleVatChange(), 1);
      },
    });
    this.addresses.billing.addToggle(this.billingLicenseeToggle);
  }

  // Shipping mode: "Different invoice address" checkbox that sits on the shipping section.
  // Unchecked => shipping+billing merged. Checked => two separate sections.
  injectBillingShippingToggle() {
    if (!this.requiresShipping) return;

    this.billingShippingToggle = new BillingShippingToggleComponent({
      checked: !this.state.isBillingShippingMerged,
      onChange: (checked) => {
        if (!this._uiInitialized) return;
        this.state.isBillingShippingMerged = !checked;
        this.applyState();
        setTimeout(() => this.handleVatChange(), 1);
      },
    });
    this.addresses.billing.addToggle(this.billingShippingToggle);
  }

  initLicenseUsageSelector() {
    if (!this.requiresLicensing) return;

    const element = this.licenseUsageSelectorElement;
    if (!element) return;

    this.licenseUsageSelector = new LicenseUsageSelectorComponent({
      element,
      onChange: ({ licenseUsage, licenseUsageClientName }) => {
        this.state.licenseUsage = licenseUsage;
        this.state.licenseUsageClientName = licenseUsageClientName;

        if (!this._uiInitialized) return;

        this.handleLicenseUsageChange();
        this.applyState();
      },
    });
  }

  handleLicenseUsageChange() {
    const { licenseUsage, licenseUsageClientName } = this.state;
    if (licenseUsage === "client") {
      this.state.isBillingLicenseeMerged = false;
      this.state.shouldIssuePartnerLicense = true; // Uncheck the opt-out checkbox
      this.addresses.licensee.setCompanyName(licenseUsageClientName);
    } else {
      this.state.isBillingLicenseeMerged = this.addresses?.licensee?.equals(
        this.addresses.billing,
      );
    }
  }

  get licenseUsageSelectorElement() {
    return /** @type {HTMLElement | null} */ (
      document.querySelector("[data-controller='cart-license-usage-selector']")
    );
  }

  //  -------------------- apply state --------------------

  // Single reducer: given `this.state`, set visibility, titles, name prefixes, mirror map.
  applyState() {
    const { isBillingLicenseeMerged, isBillingShippingMerged } = this.state;
    const { shipping, billing, licensee } = this.addresses;

    this.mirrorFrom = {};

    // License Usage Selector
    if (this.licenseUsageSelector) {
      this.licenseUsageSelector?.setLicenseUsage(this.state.licenseUsage);
      this.licenseUsageSelector?.setLicenseUsageClientName(
        this.state.licenseUsageClientName,
      );
    }

    // Different Licensee Address toggle
    this.billingLicenseeToggle?.setChecked(!isBillingLicenseeMerged);

    // Different Shipping Address toggle
    this.billingShippingToggle?.setChecked(!isBillingShippingMerged);

    // Brand Partner License toggle: only relevant when the cart contains a Branding
    // medium and the license is being issued for a client. Force-clear the state
    // outside that combo so a stale "true" cannot survive a usage-toggle.
    const partnerEligible =
      this.brandingLicense && this.state.licenseUsage === "client";
    if (!partnerEligible && this.state.shouldIssuePartnerLicense) {
      this.state.shouldIssuePartnerLicense = false;
    }
    this.partnerLicenseToggle?.setVisible(partnerEligible);
    this.partnerLicenseToggle?.setShouldIssue(
      this.state.shouldIssuePartnerLicense,
    );
    // Show the inline "Brand Partner License" PDF button next to the other
    // license buttons in the agreement label only while the opt-in is active.
    this.partnerLicenseButton?.classList.toggle(
      "hidden",
      !(partnerEligible && this.state.shouldIssuePartnerLicense),
    );

    // Billing section title reflects whether the included Brand Partner license
    // will be issued to the buyer alongside the invoice.
    if (this.brandingLicense) {
      const issuing = partnerEligible && this.state.shouldIssuePartnerLicense;
      billing.setTitle(issuing ? "Invoice and Brand Partner" : "Invoice");
    }

    // Licensee Address
    if (this.requiresLicensing) {
      const isForClient =
        this.brandingLicense && this.state.licenseUsage === "client";
      this.billingLicenseeToggle?.setIsClient(isForClient);
      licensee.setVisible(!isBillingLicenseeMerged);
      licensee.setSubmitRole("licensee");
      licensee.setTitle(
        isForClient ? "Client and License Holder" : "License Holder",
      );
      this.addressesElement
        ?.querySelector("[data-checkout-client-email]")
        ?.classList.toggle("hidden", !isForClient);
      const clientEmailInput = this.addressesElement?.querySelector(
        "[data-checkout-client-email] input",
      );
      if (clientEmailInput instanceof HTMLInputElement) {
        clientEmailInput.disabled = !isForClient;
        clientEmailInput.required = isForClient;
      }
      if (isBillingLicenseeMerged) {
        // licensee address is copied from billing address
        this.mirrorFrom.licensee = "billing";
      }
    }

    // Shipping Address
    if (this.requiresShipping) {
      shipping.setVisible(!isBillingShippingMerged);
      shipping.setSubmitRole("shipping");
      if (isBillingShippingMerged) {
        // shipping address is copied from billing address
        this.mirrorFrom.shipping = "billing";
      }
    }

    this.applyMirrors();
  }

  //  -------------------- mirror --------------------

  // Which DOM role currently mirrors from which source DOM role.

  // Copy current source values into every mirrored target (run once after
  // state changes). Per-field live mirroring happens in `handleFieldEvent`.
  applyMirrors() {
    Object.entries(this.mirrorFrom).forEach(([target, source]) => {
      this.addresses[target].copyFrom(this.addresses[source]);
    });
  }

  //  -------------------- listeners --------------------

  onAddressChange({ addressComponent, values, element }) {
    if (
      addressComponent.role === "licensee" &&
      this.state.licenseUsage === "client"
    ) {
      this.state.licenseUsageClientName = values.company;
    }

    this.handleFieldEventMirroring({ addressComponent, element });

    this.applyState();
  }

  removeListeners() {
    Object.values(this.addresses).forEach((address) => {
      address.removeListeners();
    });

    this.billingShippingToggle?.removeListeners();
    this.billingLicenseeToggle?.removeListeners();
    this.partnerLicenseToggle?.removeListeners();
  }

  // Delegated field event: runs mirroring + triggers VAT refresh when billing
  // country/vat_id changes.
  handleFieldEventMirroring({ addressComponent, element }) {
    if (!(element instanceof HTMLElement)) return;

    const srcRole = addressComponent.role;
    const src = this.addresses[srcRole];
    const attr = attrFromName(/** @type {any} */ (element).name);
    if (!src || !attr) return;

    // Mirror into every target that currently mirrors from this source.
    Object.entries(this.mirrorFrom).forEach(([target, source]) => {
      if (source !== srcRole) return;
      this.addresses[target].copyAttrFrom(src, attr);
    });

    // VAT cares about billing country + vat_id. We use the submit role so
    // that in shipping mode, typing into a merged shipping section (which
    // submits as shipping but mirrors into billing) still triggers a VAT
    // refresh via the second branch below.
    const isVatAttr = attr === "country" || attr === "vat_id";
    if (!isVatAttr) return;
    if (src.submitRole === "billing" || this.mirrorFrom.billing === srcRole) {
      setTimeout(() => this.handleVatChange(), 1);
    }
  }

  //  -------------------- VAT --------------------

  // POSTs the form to the VAT update endpoint. The server response is a JSON needed for
  // updating the VAT row + the "I agree to VAT reverse charge" section.
  handleVatChange() {
    const vatUrl = /** @type {CheckoutFormControllerInstance} */ (
      /** @type {unknown} */ (this)
    ).vatUpdateUrlValue;
    if (!vatUrl) {
      console.error(
        "checkout-form: missing data-checkout-form-vat-update-url-value",
      );
      return;
    }

    fetch(vatUrl, {
      method: "PUT",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
        Accept: "application/json",
      },
      body: new FormData(this.form),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`VAT update failed: ${r.statusText}`);
        return r.json();
      })
      .then((data) => this.applyVatUpdate(data))
      .catch((err) => console.error("Error updating VAT:", err));
  }

  // Applies the VAT update payload to the cart DOM.
  applyVatUpdate(data) {
    const vatPriceEl = document.getElementById("vat-price");
    if (vatPriceEl) {
      vatPriceEl.textContent = data.vat_price.formatted;
    } else {
      console.error("#vat-price element not found");
    }

    const shippingPriceEl = document.getElementById("shipping-price");
    if (shippingPriceEl) {
      shippingPriceEl.textContent = data.shipping_price.formatted;
    } else {
      console.error("#shipping-price element not found");
    }

    const totalPriceEl = document.getElementById("total-price");
    if (totalPriceEl) {
      totalPriceEl.textContent = data.total_price.formatted;
    } else {
      console.error("#total-price element not found");
    }

    // Toggle the "I agree to VAT reverse charge" section. When reverse-charged,
    // show it; otherwise hide it and uncheck its checkbox so a stale agreement
    // is not submitted.
    const vatAgreement = document.querySelector(".vat__agreement");
    if (vatAgreement) {
      if (data.vat_reverse_charged) {
        vatAgreement.classList.remove("hidden");
      } else {
        vatAgreement
          .querySelectorAll('input[type="checkbox"]')
          .forEach((input) => {
            /** @type {HTMLInputElement} */ (input).checked = false;
          });
        vatAgreement.classList.add("hidden");
      }
    } else {
      console.error(".vat__agreement element not found");
    }

    // Hide the agreements section entirely when it has no visible content
    // (i.e. no licensing required and no VAT reverse charge agreement).
    const agreementsSection = /** @type {HTMLElement | null} */ (
      document.querySelector(".agreements-section")
    );
    if (agreementsSection) {
      const hasVisibleContent =
        this.requiresLicensing || data.vat_reverse_charged;
      agreementsSection.classList.toggle("hidden", !hasVisibleContent);
    } else {
      console.error(".agreements-section element not found");
    }
  }
}
